/* Feb-2026 · Admin — Author Publishing Agreements queue.
 *
 * Lists every author signature awaiting Company countersignature,
 * with a one-click Approve + Countersign flow that saves the exec'd
 * PDF into the row and a Reject flow that kicks the row back to the
 * author with a reason.
 *
 * Rendered under the `author-agreements` tab in AdminPage.jsx.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  ScrollText, PenTool, Loader2, Download, X, Check, RefreshCw,
} from 'lucide-react';
import { adminApi } from '@/lib/api';

const TABS = [
  { id: 'pending_admin', label: 'Pending' },
  { id: 'executed',      label: 'Executed' },
  { id: 'rejected',      label: 'Rejected' },
  { id: '',              label: 'All' },
];

const STATUS_TONE = {
  pending_admin: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
  executed:      'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
  rejected:      'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40',
};

export default function AuthorAgreementsPanel() {
  const [statusFilter, setStatusFilter] = useState('pending_admin');
  const [rows, setRows]     = useState([]);
  const [tallies, setTallies] = useState({});
  const [version, setVersion] = useState('');
  const [loading, setLoading] = useState(false);
  const [openRow, setOpenRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const r = await adminApi.publishingAgreementList(params);
      setRows(r.agreements || []);
      setTallies(r.tallies || {});
      setVersion(r.current_version || '');
    } catch (_e) { toast.error('Failed to load agreements.'); }
    finally { setLoading(false); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4" data-testid="apa-admin-panel">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 text-[14px] font-semibold">
          <ScrollText className="h-4 w-4 text-primary" />
          Author Publishing Agreements
          {version && (
            <span className="rounded-sm border border-border bg-panel-header/50 px-1.5 py-[1px] text-[10.5px] font-normal text-muted-foreground">
              current: {version}
            </span>
          )}
        </div>
        <div className="ml-auto flex items-center gap-1">
          {TABS.map(({ id, label }) => (
            <button
              key={id || 'all'}
              type="button"
              onClick={() => setStatusFilter(id)}
              data-testid={`apa-admin-filter-${id || 'all'}`}
              className={`inline-flex items-center gap-1 rounded-sm border px-2 py-1 text-[11px] font-semibold ${
                statusFilter === id
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-background text-muted-foreground hover:bg-secondary'
              }`}
            >
              {label}
              {tallies[id] != null && (
                <span className="rounded-full bg-secondary px-1.5 text-[10px]">{tallies[id]}</span>
              )}
            </button>
          ))}
          <button
            type="button"
            onClick={load}
            data-testid="apa-admin-refresh"
            className="ml-2 inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11px] text-muted-foreground hover:bg-secondary"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Refresh
          </button>
        </div>
      </div>

      {rows.length === 0 && !loading && (
        <div className="rounded-md border border-dashed border-border bg-panel-header/40 p-6 text-center text-[12.5px] text-muted-foreground">
          No agreements in this bucket.
        </div>
      )}

      <div className="grid gap-2">
        {rows.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => setOpenRow(r)}
            data-testid="apa-admin-row-open"
            className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-background p-2.5 text-left hover:border-primary/60"
          >
            <div className="min-w-[220px]">
              <div className="text-[12.5px] font-semibold">
                {r.author_name || r.author_email || r.user_id}
              </div>
              <div className="text-[10.5px] text-muted-foreground">
                {r.author_email} · {r.id}
              </div>
            </div>
            <div className={`inline-flex items-center gap-1 rounded-full border px-2 py-[2px] text-[10.5px] font-semibold ${STATUS_TONE[r.status] || 'border-border'}`}>
              {r.status?.replace(/_/g, ' ')}
            </div>
            <div className="text-[10.5px] text-muted-foreground">
              v {r.contract_version} · signed {r.author_signed_at?.slice(0, 10)}
              {r.admin_signed_at && (<> · countersigned {r.admin_signed_at.slice(0, 10)}</>)}
            </div>
            <div className="ml-auto text-[11px] text-primary underline">Open →</div>
          </button>
        ))}
      </div>

      {openRow && (
        <AgreementDetailDrawer
          agreement={openRow}
          onClose={() => setOpenRow(null)}
          onChanged={() => { setOpenRow(null); load(); }}
        />
      )}
    </div>
  );
}

function AgreementDetailDrawer({ agreement, onClose, onChanged }) {
  const [full, setFull]         = useState(null);
  const [busy, setBusy]         = useState(false);
  const [mySignature, setMySig] = useState('');
  const [displayRole, setRole]  = useState('Authorised Officer');
  const [rejectReason, setRR]   = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.publishingAgreementGet(agreement.id);
        setFull(r.agreement);
      } catch { setFull(agreement); }
    })();
  }, [agreement.id]);      // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async () => {
    if (mySignature.trim().length < 2) return toast.error('Type your legal name to countersign.');
    setBusy(true);
    try {
      await adminApi.publishingAgreementApprove(agreement.id, {
        signature: mySignature.trim(),
        display_role: displayRole.trim() || 'Authorised Officer',
      });
      toast.success('Countersigned. PDF is generated and cached on the row.');
      onChanged();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((typeof d === 'string' ? d : d?.message) || 'Countersignature failed.');
    } finally { setBusy(false); }
  };

  const reject = async () => {
    if (rejectReason.trim().length < 3) return toast.error('Give the author a short reason (3+ chars).');
    setBusy(true);
    try {
      await adminApi.publishingAgreementReject(agreement.id, { reason: rejectReason.trim() });
      toast.success('Rejected — author has been notified on their dashboard.');
      onChanged();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((typeof d === 'string' ? d : d?.message) || 'Rejection failed.');
    } finally { setBusy(false); }
  };

  const canApprove = full?.status === 'pending_admin';
  const canReject  = full?.status === 'pending_admin';
  const pdfUrl     = adminApi.publishingAgreementPdfUrl(agreement.id);

  return (
    <div className="fixed inset-0 z-[70] flex items-stretch justify-end bg-black/50" data-testid="apa-admin-drawer" onClick={onClose}>
      <div
        className="flex h-full w-full max-w-3xl flex-col overflow-hidden bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-border p-3">
          <ScrollText className="h-4 w-4 text-primary" />
          <div className="text-[13px] font-semibold">
            {full?.author_name || full?.author_email || agreement.id}
          </div>
          <div className={`inline-flex rounded-full border px-2 py-[2px] text-[10.5px] font-semibold ${STATUS_TONE[full?.status] || 'border-border'}`}>
            {full?.status?.replace(/_/g, ' ')}
          </div>
          <button onClick={onClose} className="ml-auto rounded-sm p-1 hover:bg-secondary"><X className="h-4 w-4" /></button>
        </div>

        <div className="grid grid-cols-2 gap-2 border-b border-border p-3 text-[11px]">
          <div><span className="text-muted-foreground">Author signed:</span> {full?.author_signed_at || '—'} ({full?.author_ip || '—'})</div>
          <div><span className="text-muted-foreground">Version:</span> {full?.contract_version || '—'}</div>
          <div className="col-span-2 truncate"><span className="text-muted-foreground">SHA-256:</span> <span className="font-mono">{full?.content_sha256 || '—'}</span></div>
          {full?.admin_signed_at && (
            <>
              <div><span className="text-muted-foreground">Countersigned:</span> {full.admin_signed_at} ({full.admin_ip})</div>
              <div><span className="text-muted-foreground">By:</span> {full.admin_display || full.admin_user}</div>
            </>
          )}
          {full?.rejection_reason && (
            <div className="col-span-2 rounded-md bg-red-500/10 p-2 text-red-700 dark:text-red-300">
              <strong>Rejection reason:</strong> {full.rejection_reason}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-auto p-3">
          <pre className="whitespace-pre-wrap font-serif text-[12px] leading-relaxed">
            {full?.contract_text || 'Loading…'}
          </pre>
        </div>

        <div className="border-t border-border p-3">
          {full?.status === 'executed' && (
            <a
              href={pdfUrl} target="_blank" rel="noreferrer"
              data-testid="apa-admin-download-pdf"
              className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-[12px] font-semibold text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300"
            >
              <Download className="h-3.5 w-3.5" /> Download signed PDF
            </a>
          )}
          {canApprove && (
            <div className="grid gap-2">
              <div className="grid gap-2 md:grid-cols-2">
                <label>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Your legal name</div>
                  <input
                    value={mySignature}
                    onChange={(e) => setMySig(e.target.value)}
                    placeholder="Your name"
                    data-testid="apa-admin-my-signature"
                    className="mt-1 h-8 w-full rounded-sm border border-border bg-background px-2 text-[12.5px] font-serif italic"
                  />
                </label>
                <label>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Role (for the PDF)</div>
                  <input
                    value={displayRole}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="Authorised Officer"
                    data-testid="apa-admin-display-role"
                    className="mt-1 h-8 w-full rounded-sm border border-border bg-background px-2 text-[12.5px]"
                  />
                </label>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button" onClick={approve} disabled={busy}
                  data-testid="apa-admin-countersign-btn"
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><PenTool className="h-3.5 w-3.5" /> Approve & Countersign</>}
                </button>
                {canReject && (
                  <>
                    <input
                      value={rejectReason}
                      onChange={(e) => setRR(e.target.value)}
                      placeholder="Rejection reason…"
                      data-testid="apa-admin-reject-reason"
                      className="h-8 w-64 rounded-sm border border-border bg-background px-2 text-[12px]"
                    />
                    <button
                      type="button" onClick={reject} disabled={busy}
                      data-testid="apa-admin-reject-btn"
                      className="inline-flex items-center gap-1 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-[12px] font-semibold text-red-700 hover:bg-red-500/20 dark:text-red-300 disabled:opacity-50"
                    >
                      <X className="h-3.5 w-3.5" /> Reject
                    </button>
                  </>
                )}
                <div className="ml-auto text-[10.5px] text-muted-foreground">
                  <Check className="mr-1 inline h-3 w-3" />
                  You are signing on behalf of Crown & Rose LLC.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
